import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { whatsappUrl } from "@/lib/site";
import {
  CONVERSA,
  CONVERSA_WHATSAPP_MSG,
  formatDateBR,
  isPlaceholder,
} from "@/lib/metodo-conversa";
import mockup from "@/assets/conversa-ebook-mockup.png";

const TITLE = "Venda no WhatsApp - Método CONVERSA | Guia Digital SVG Multimídia";
const DESC =
  "Guia digital de 41 páginas com o Método CONVERSA: abordagem, diagnóstico, valor, objeções, fechamento e follow-up para transformar conversas do WhatsApp em vendas. De R$ 249,00 por R$ 67,90.";

export const Route = createFileRoute("/venda-no-whatsapp-metodo-conversa")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "product" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        property: "og:url",
        content: "https://www.svgmultimidia.com.br/venda-no-whatsapp-metodo-conversa",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://www.svgmultimidia.com.br/venda-no-whatsapp-metodo-conversa",
      },
    ],
  }),
  component: MetodoConversaPage,
});

/* ---------------------------------------------------------------- tokens -- */
const NAVY = "#0B1F33";
const NAVY_2 = "#102A43";
const GREEN = "#19B67A";
const GREEN_DARK = "#0D8F61";
const GREEN_LIGHT = "#E8F8F1";
const CORAL = "#FF6B5E";
const GOLD = "#F4B942";

/* -------------------------------------------------------------- helpers --- */
const checkoutHref = isPlaceholder(CONVERSA.checkoutUrl)
  ? whatsappUrl(CONVERSA_WHATSAPP_MSG)
  : CONVERSA.checkoutUrl;
const checkoutExternal = isPlaceholder(CONVERSA.checkoutUrl);
const offerDateLabel = formatDateBR(CONVERSA.ofertaAte);

function trackCta(label: string) {
  const w = window as unknown as {
    gtag?: (...a: unknown[]) => void;
    fbq?: (...a: unknown[]) => void;
    dataLayer?: unknown[];
  };
  w.gtag?.("event", "cta_click", { event_category: "metodo_conversa", event_label: label });
  w.fbq?.("track", "InitiateCheckout", { content_name: "Método CONVERSA", value: 67.9, currency: "BRL" });
  w.dataLayer?.push({ event: "conversa_cta_click", cta: label });
}

function CTA({
  label,
  size = "md",
  full = false,
}: {
  label: string;
  size?: "md" | "lg";
  full?: boolean;
}) {
  return (
    <a
      href={checkoutHref}
      {...(checkoutExternal ? { target: "_blank", rel: "noopener" } : {})}
      onClick={() => trackCta(label)}
      className={`inline-flex items-center justify-center rounded-full text-center font-extrabold uppercase tracking-wide text-white shadow-[0_16px_40px_-12px_rgba(25,182,122,0.75)] transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0D8F61] ${
        size === "lg" ? "px-8 py-5 text-base md:text-lg" : "px-6 py-4 text-sm md:text-base"
      } ${full ? "w-full" : ""}`}
      style={{ background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)` }}
    >
      {label}
    </a>
  );
}

function Safety() {
  return (
    <p className="mt-3 text-xs font-medium text-[#4A6076] md:text-sm">
      Compra segura • Produto digital • Acesso após a confirmação
    </p>
  );
}

/* ------------------------------------------------------------- selos ----- */
function GuaranteeSeal() {
  const days = CONVERSA.garantiaDias ?? 7;
  return (
    <div className="relative shrink-0" aria-hidden>
      <div
        className="grid h-36 w-36 place-items-center rounded-full shadow-[0_20px_45px_-20px_rgba(212,154,30,0.8)]"
        style={{ background: `conic-gradient(from 210deg, ${GOLD}, #F7D794, #D89A1E, ${GOLD})` }}
      >
        <div className="grid h-[7.25rem] w-[7.25rem] place-items-center rounded-full border-[3px] border-dashed border-[#8A6410]/40 bg-white text-center">
          <div>
            <p className="text-4xl font-black leading-none" style={{ color: "#9A6C08" }}>
              {days}
            </p>
            <p className="text-[11px] font-black uppercase tracking-[0.16em]" style={{ color: "#9A6C08" }}>
              dias
            </p>
            <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.14em] text-[#42607A]">
              Garantia total
            </p>
          </div>
        </div>
      </div>
      <span
        className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white shadow-md"
        style={{ background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)` }}
      >
        Risco zero
      </span>
    </div>
  );
}

const icon = "h-5 w-5";
function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={icon} aria-hidden>
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 1 1 8 0v3" />
    </svg>
  );
}
function CardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={icon} aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </svg>
  );
}
function BoltIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={icon} aria-hidden>
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
    </svg>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em]"
      style={{ background: GREEN_LIGHT, color: GREEN_DARK }}
    >
      {children}
    </span>
  );
}

function Wrap({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`mx-auto w-full max-w-[1180px] px-5 ${className}`}>{children}</div>;
}

/* --------------------------------------------------------------- content -- */
const PROBLEMS = [
  "O cliente pede o preço e desaparece.",
  "A conversa começa bem, mas não chega ao fechamento.",
  "Você não sabe o que perguntar ao lead.",
  "O atendimento depende da improvisação.",
  "Você envia propostas e não recebe resposta.",
  "O follow-up parece cobrança ou insistência.",
  "Você tem bons produtos, mas dificuldade para conduzir a decisão.",
  "Leads antigos ficam esquecidos na lista de contatos.",
];

const STEPS = [
  { l: "C", t: "Contextualizar", d: "Abrir a conversa com clareza sobre quem fala, por que fala e o que vem a seguir." },
  { l: "O", t: "Obter permissão", d: "Conquistar o aceite do lead antes de avançar, evitando mensagens invasivas." },
  { l: "N", t: "Necessidade", d: "Perguntas de diagnóstico que revelam o problema real antes de qualquer oferta." },
  { l: "V", t: "Valor", d: "Apresentar a solução conectada ao que o cliente disse — não a um script genérico." },
  { l: "E", t: "Evidência", d: "Provas, exemplos e comparações que sustentam a promessa feita." },
  { l: "R", t: "Reduzir risco", d: "Eliminar o medo da decisão com condições, garantias e próximos passos claros." },
  { l: "S", t: "Solicitar decisão", d: "Pedir o fechamento de forma direta e respeitosa, com uma resposta fácil." },
  { l: "A", t: "Acompanhar", d: "Cadência de follow-up com contexto e utilidade, sem parecer cobrança." },
];

const ANTES = [
  "Atendimento improvisado",
  "Mensagens longas e genéricas",
  "Preço enviado sem construção de valor",
  "Dificuldade para responder objeções",
  "Leads esquecidos",
  "Follow-ups que parecem cobrança",
  "Conversas sem próximo passo",
];

const DEPOIS = [
  "Processo comercial organizado",
  "Mensagens claras e personalizadas",
  "Diagnóstico antes da oferta",
  "Valor percebido antes do preço",
  "Respostas estruturadas para objeções",
  "Follow-up com contexto e utilidade",
  "Fechamento conduzido com segurança",
];

const ENTREGAS = [
  { t: `Guia digital completo`, d: `${CONVERSA.paginas} páginas em PDF, direto ao ponto e fácil de aplicar.` },
  { t: "Método CONVERSA passo a passo", d: "Os 8 movimentos explicados com exemplos reais de mensagem." },
  { t: "Estratégias de comunicação comercial", d: "Como escrever para vender sem soar robótico ou agressivo." },
  { t: "Perguntas de diagnóstico", d: "O que perguntar para entender o cenário antes de ofertar." },
  { t: "Técnicas para apresentar valor", d: "Construção de percepção antes de falar em preço." },
  { t: "Textos, áudios, imagens e propostas", d: "Quando usar cada formato e como estruturar cada um." },
  { t: "Método para responder objeções", d: "Estrutura pronta para preço, tempo, confiança e concorrência." },
  { t: "Técnicas de fechamento", d: "Como pedir a decisão com segurança e respeito." },
  { t: "Cadência de follow-up", d: "Quando e como retomar sem parecer insistência." },
  { t: "Biblioteca de mensagens", d: "Modelos prontos para recuperar leads parados." },
  { t: "Script 1 — serviços", d: "Venda consultiva completa para serviços de maior valor." },
  { t: "Script 2 — produtos", d: "Conversa de compra rápida para produtos físicos ou digitais." },
  { t: "Script 3 — reativação", d: "Retomada de leads antigos e propostas paradas." },
  { t: "Rotina comercial de 45 minutos", d: "O bloco diário que mantém o funil vivo." },
  { t: "Indicadores e métricas", d: "O que medir para saber onde a venda está travando." },
  { t: "Plano de implementação em 7 dias", d: "Ordem exata de execução na primeira semana." },
  { t: "Checklist e laboratório", d: "Ferramenta prática para montar suas próprias mensagens." },
];

const SCRIPTS = [
  {
    n: "Script 1",
    t: "Venda consultiva de serviços",
    d: "Para agências, consultores, profissionais autônomos, clínicas, negócios locais e serviços de maior valor.",
  },
  {
    n: "Script 2",
    t: "Venda de produtos físicos ou digitais",
    d: "Para lojas, e-commerce, cursos, treinamentos, produtos digitais e ofertas de compra rápida.",
  },
  {
    n: "Script 3",
    t: "Reativação de leads antigos",
    d: "Para propostas paradas, clientes indecisos, contatos antigos e oportunidades esquecidas.",
  },
];

const FOLLOWUPS = [
  "Oi, [nome]. A conversa parou depois do investimento. Para eu não presumir: o ponto foi orçamento, prioridade ou valor percebido?",
  "Qual cenário descreve melhor seu momento: 1) quero avançar; 2) preciso ajustar; 3) não é o momento?",
  "Como não consegui retorno, vou encerrar este acompanhamento para não ser inconveniente. Se voltar a ser prioridade, pode me chamar por aqui.",
];

const PARA_QUEM = [
  "Vende produtos ou serviços pelo WhatsApp",
  "Trabalha como autônomo ou consultor",
  "Possui uma loja física ou digital",
  "Atende leads vindos de anúncios",
  "Vende cursos, mentorias ou infoprodutos",
  "Trabalha em uma equipe comercial",
  "Quer recuperar conversas paradas",
  "Deseja vender melhor sem mensagens agressivas",
];

const VALOR_STACK = [
  { t: "Método completo", v: "R$ 97" },
  { t: "3 scripts adaptáveis", v: "R$ 87" },
  { t: "Biblioteca de follow-ups", v: "R$ 67" },
  { t: "Checklists, métricas e plano de ação", v: "R$ 49" },
];

const FAQS = [
  { q: "Como receberei o material?", a: "Após a confirmação do pagamento você recebe o acesso digital ao PDF pelo e-mail informado na compra." },
  { q: "O produto é físico ou digital?", a: "É 100% digital: um guia em PDF com 41 páginas. Não há envio de material impresso." },
  { q: "Funciona para produtos e serviços?", a: "Sim. O guia traz um script para venda consultiva de serviços e outro para produtos físicos ou digitais." },
  { q: "Os scripts podem ser adaptados?", a: "Sim. Todos os scripts possuem campos entre colchetes para você preencher com o seu contexto, oferta e público." },
  { q: "Serve para quem está começando?", a: "Sim. O método parte do zero: como abrir a conversa, o que perguntar e como conduzir até o fechamento." },
  { q: "Serve para equipes comerciais?", a: "Sim. O material pode ser usado como padrão de atendimento para alinhar toda a equipe na mesma linguagem." },
  { q: "Preciso utilizar o WhatsApp Business?", a: "Não é obrigatório. O método funciona em qualquer versão do WhatsApp, mas o Business facilita etiquetas e respostas rápidas." },
  { q: "Em quanto tempo consigo aplicar?", a: "Há um plano de implementação em 7 dias. Os primeiros scripts podem ser usados no mesmo dia da leitura." },
  { q: "Existe garantia?", a: CONVERSA.garantiaDias ? `Sim. Você tem ${CONVERSA.garantiaDias} dias para conhecer o material, conforme as condições informadas no checkout.` : "As condições de garantia são as informadas na página de checkout no momento da compra." },
  { q: "Como funciona o pagamento?", a: "O pagamento é feito em ambiente de checkout seguro. O acesso é liberado após a confirmação." },
  { q: "Os resultados são garantidos?", a: "Não. O material entrega método, scripts e direcionamento, mas os resultados dependem da sua oferta, do seu mercado, da qualidade dos leads e da execução." },
];

/* ------------------------------------------------------------- countdown -- */
function useCountdown(iso: string | null) {
  const [state, setState] = useState<{ ready: boolean; ended: boolean; d: number; h: number; m: number; s: number }>({
    ready: false, ended: false, d: 0, h: 0, m: 0, s: 0,
  });
  useEffect(() => {
    if (!iso) return;
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setState({ ready: true, ended: true, d: 0, h: 0, m: 0, s: 0 });
        return;
      }
      setState({
        ready: true,
        ended: false,
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [iso]);
  return state;
}

/* ------------------------------------------------------------------ page -- */
function MetodoConversaPage() {
  const cd = useCountdown(CONVERSA.ofertaAte);

  return (
    <div className="bg-white text-[#0B1F33]">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Product",
                name: "Venda no WhatsApp - Método CONVERSA",
                description: DESC,
                brand: { "@type": "Brand", name: "SVG Multimídia" },
                offers: {
                  "@type": "Offer",
                  price: "67.90",
                  priceCurrency: "BRL",
                  availability: "https://schema.org/InStock",
                  url: "https://www.svgmultimidia.com.br/venda-no-whatsapp-metodo-conversa",
                },
              },
              {
                "@type": "FAQPage",
                mainEntity: FAQS.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      {/* ------------------------------------------------------ offer bar -- */}
      <div className="text-white" style={{ background: `linear-gradient(90deg, ${CORAL} 0%, #E0483A 100%)` }}>
        <Wrap className="flex flex-col items-center gap-1 py-2.5 text-center md:flex-row md:justify-center md:gap-4 md:py-3">
          <span className="text-sm font-extrabold uppercase tracking-wide md:text-base">
            Oferta especial: de {CONVERSA.precoDe} por apenas {CONVERSA.precoPor}
          </span>
          {offerDateLabel && (
            <span className="text-xs font-medium text-white/90 md:text-sm">
              {cd.ended
                ? "Condição promocional encerrada."
                : `Condição promocional disponível até ${offerDateLabel}.`}
            </span>
          )}
        </Wrap>
      </div>

      {/* --------------------------------------------------------- header -- */}
      <header className="border-b border-white/10" style={{ background: NAVY }}>
        <Wrap className="flex items-center justify-between py-4">
          {/* Substituir por <img src={logo} .../> quando o logotipo oficial estiver disponível */}
          <Link to="/" className="flex items-center gap-2.5">
            <span
              className="grid h-9 w-9 place-items-center rounded-xl text-sm font-black text-white"
              style={{ background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)` }}
            >
              SVG
            </span>
            <span className="text-sm font-bold text-white md:text-base">
              SVG <span className="font-light text-white/70">Multimídia</span>
            </span>
          </Link>
          <div className="hidden md:block">
            <CTA label="Quero acessar o Método CONVERSA" />
          </div>
        </Wrap>
      </header>

      {/* ----------------------------------------------------------- hero -- */}
      <section
        className="relative overflow-hidden"
        style={{ background: `radial-gradient(120% 90% at 15% 0%, ${NAVY_2} 0%, ${NAVY} 62%)` }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 top-10 h-80 w-80 rounded-full opacity-25 blur-3xl"
          style={{ background: GREEN }}
        />
        <Wrap className="relative grid items-center gap-12 py-14 md:py-20 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <span
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white"
              style={{ background: "rgba(25,182,122,0.18)", color: "#7CE9BC" }}
            >
              Guia digital • {CONVERSA.paginas} páginas
            </span>
            <h1 className="mt-5 text-3xl font-black leading-[1.1] tracking-tight text-white sm:text-4xl md:text-5xl">
              Transforme conversas no WhatsApp em{" "}
              <span style={{ color: "#4FE3A6" }}>vendas</span> com um método simples,
              estratégico e fácil de aplicar
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#C6D6E4] md:text-lg">
              Aprenda a abordar, diagnosticar, apresentar sua oferta, responder objeções,
              fechar vendas e recuperar leads que pararam de responder.
            </p>
            <ul className="mt-7 grid gap-2.5 sm:grid-cols-2">
              {[
                "Método prático",
                "3 scripts completos",
                "Biblioteca de follow-ups",
                "Para produtos e serviços",
                "Acesso digital após a confirmação",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm font-medium text-white">
                  <span
                    className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-black text-[#062318]"
                    style={{ background: GREEN }}
                  >
                    ✓
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur">
              <div className="flex flex-wrap items-end gap-x-4 gap-y-1">
                <span className="text-sm text-[#9FB4C7] line-through">De {CONVERSA.precoDe}</span>
                <span className="text-4xl font-black text-white md:text-5xl">{CONVERSA.precoPor}</span>
                <span
                  className="rounded-full px-2.5 py-1 text-xs font-black text-[#062318]"
                  style={{ background: GOLD }}
                >
                  -{CONVERSA.descontoPercentual}%
                </span>
              </div>
              {CONVERSA.parcelamento && (
                <p className="mt-1 text-sm text-[#C6D6E4]">{CONVERSA.parcelamento}</p>
              )}
              <p className="mt-1 text-sm text-[#C6D6E4]">
                Economize aproximadamente {CONVERSA.descontoPercentual}%
              </p>
              <div className="mt-5">
                <CTA label="Quero vender mais pelo WhatsApp" size="lg" full />
              </div>
              <p className="mt-3 text-center text-xs font-medium text-[#9FB4C7]">
                Compra segura • Produto digital • Acesso após a confirmação
              </p>
            </div>
          </div>

          <div className="relative mx-auto max-w-sm lg:max-w-none">
            <div
              aria-hidden
              className="absolute inset-8 rounded-full opacity-40 blur-3xl"
              style={{ background: GREEN_DARK }}
            />
            <img
              src={mockup}
              alt="Mockup do guia digital Método CONVERSA em PDF"
              width={1024}
              height={1024}
              className="relative w-full drop-shadow-[0_40px_60px_rgba(0,0,0,0.45)]"
            />
          </div>
        </Wrap>
      </section>

      {/* ------------------------------------------------------- countdown -- */}
      {offerDateLabel && cd.ready && (
        <div style={{ background: GREEN_LIGHT }}>
          <Wrap className="flex flex-col items-center justify-center gap-3 py-4 text-center md:flex-row">
            {cd.ended ? (
              <p className="text-sm font-bold text-[#0D8F61]">
                A condição promocional de lançamento foi encerrada em {offerDateLabel}.
              </p>
            ) : (
              <>
                <span className="text-sm font-bold uppercase tracking-wide text-[#0D8F61]">
                  Condição válida até {offerDateLabel}
                </span>
                <div className="flex gap-2">
                  {[
                    { v: cd.d, l: "dias" },
                    { v: cd.h, l: "horas" },
                    { v: cd.m, l: "min" },
                    { v: cd.s, l: "seg" },
                  ].map((u) => (
                    <div
                      key={u.l}
                      className="min-w-14 rounded-xl px-3 py-2 text-center text-white"
                      style={{ background: NAVY }}
                    >
                      <div className="text-lg font-black leading-none">{String(u.v).padStart(2, "0")}</div>
                      <div className="mt-1 text-[10px] uppercase tracking-wide text-white/60">{u.l}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Wrap>
        </div>
      )}

      {/* --------------------------------------------------------- problema -- */}
      <section className="bg-[#F7F9FB] py-16 md:py-24">
        <Wrap>
          <Reveal className="max-w-3xl">
            <Eyebrow>O diagnóstico</Eyebrow>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-4xl">
              Quantas vendas você está perdendo dentro do próprio WhatsApp?
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p} delay={(i % 3) as 0 | 1 | 2}>
                <div className="h-full rounded-2xl border border-[#0B1F33]/10 bg-white p-5 shadow-[0_12px_30px_-18px_rgba(11,31,51,0.45)] transition-transform duration-200 hover:-translate-y-1">
                  <span
                    className="grid h-8 w-8 place-items-center rounded-lg text-sm font-black"
                    style={{ background: "#FFECEA", color: CORAL }}
                  >
                    !
                  </span>
                  <p className="mt-3 text-sm font-semibold leading-relaxed text-[#1D3B55]">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p
              className="mt-10 rounded-2xl border-l-4 p-6 text-lg font-bold md:text-xl"
              style={{ background: GREEN_LIGHT, borderColor: GREEN, color: NAVY }}
            >
              O problema nem sempre está no produto. Muitas vezes, está na forma como a conversa
              é conduzida.
            </p>
          </Reveal>
        </Wrap>
      </section>

      {/* ---------------------------------------------------------- método -- */}
      <section className="py-16 md:py-24">
        <Wrap>
          <Reveal className="max-w-3xl">
            <Eyebrow>O método</Eyebrow>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-4xl">
              Conheça o Método CONVERSA
            </h2>
            <p className="mt-4 text-base text-[#42607A] md:text-lg">
              O Método CONVERSA organiza a venda em oito movimentos e ajuda você a saber o que
              dizer, quando perguntar, como apresentar valor e qual próximo passo solicitar.
            </p>
          </Reveal>
          <div className="relative mt-12">
            <div
              aria-hidden
              className="absolute left-0 right-0 top-7 hidden h-0.5 lg:block"
              style={{ background: `linear-gradient(90deg, ${GREEN}00, ${GREEN}, ${GREEN}00)` }}
            />
            <div className="relative grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <Reveal key={s.l} delay={(i % 4) as 0 | 1 | 2 | 3}>
                  <div className="h-full rounded-2xl border border-[#0B1F33]/10 bg-white p-5 shadow-[0_16px_40px_-24px_rgba(11,31,51,0.6)] transition-transform duration-200 hover:-translate-y-1">
                    <span
                      className="grid h-14 w-14 place-items-center rounded-2xl text-2xl font-black text-white shadow-[0_10px_24px_-10px_rgba(13,143,97,0.9)]"
                      style={{ background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)` }}
                    >
                      {s.l}
                    </span>
                    <h3 className="mt-4 text-lg font-extrabold">{s.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#42607A]">{s.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="mt-10 text-center">
            <CTA label="Quero acessar o Método CONVERSA" size="lg" />
            <Safety />
          </div>
        </Wrap>
      </section>

      {/* -------------------------------------------------- transformação -- */}
      <section className="py-16 md:py-24" style={{ background: NAVY }}>
        <Wrap>
          <Reveal className="max-w-3xl">
            <span
              className="inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em]"
              style={{ background: "rgba(25,182,122,0.18)", color: "#7CE9BC" }}
            >
              A transformação
            </span>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-4xl">
              Antes e depois do Método CONVERSA
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.05] p-6">
                <h3 className="text-lg font-extrabold" style={{ color: CORAL }}>Antes</h3>
                <ul className="mt-4 space-y-3">
                  {ANTES.map((a) => (
                    <li key={a} className="flex gap-3 text-sm text-[#C6D6E4]">
                      <span style={{ color: CORAL }}>✕</span>
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={1}>
              <div
                className="h-full rounded-2xl border p-6"
                style={{ borderColor: "rgba(25,182,122,0.45)", background: "rgba(25,182,122,0.10)" }}
              >
                <h3 className="text-lg font-extrabold" style={{ color: "#7CE9BC" }}>Depois</h3>
                <ul className="mt-4 space-y-3">
                  {DEPOIS.map((a) => (
                    <li key={a} className="flex gap-3 text-sm font-medium text-white">
                      <span style={{ color: "#7CE9BC" }}>✓</span>
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </Wrap>
      </section>

      {/* -------------------------------------------------------- entregas -- */}
      <section className="py-16 md:py-24">
        <Wrap>
          <Reveal className="max-w-3xl">
            <Eyebrow>O que você recebe</Eyebrow>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-4xl">
              Tudo o que você precisa para vender pelo WhatsApp com mais segurança
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ENTREGAS.map((e, i) => (
              <Reveal key={e.t} delay={(i % 3) as 0 | 1 | 2}>
                <div className="h-full rounded-2xl border border-[#0B1F33]/10 bg-white p-5 shadow-[0_14px_34px_-22px_rgba(11,31,51,0.55)] transition-transform duration-200 hover:-translate-y-1">
                  <div className="flex items-start gap-3">
                    <span
                      className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg text-xs font-black text-[#062318]"
                      style={{ background: GREEN }}
                    >
                      ✓
                    </span>
                    <div>
                      <h3 className="text-base font-extrabold leading-snug">{e.t}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-[#42607A]">{e.d}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* mockups de páginas internas */}
          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {[
              { t: "Método CONVERSA", s: "Os 8 movimentos da venda" },
              { t: "Scripts prontos", s: "Serviços, produtos e reativação" },
              { t: "Follow-up", s: "Biblioteca de mensagens" },
            ].map((p, i) => (
              <Reveal key={p.t} delay={(i % 3) as 0 | 1 | 2}>
                <div className="rounded-2xl border border-[#0B1F33]/10 bg-white p-5 shadow-[0_20px_45px_-28px_rgba(11,31,51,0.7)]">
                  <div className="rounded-xl p-4" style={{ background: "#F2F6F9" }}>
                    <div className="h-2 w-16 rounded-full" style={{ background: GREEN }} />
                    <div className="mt-3 h-2.5 w-4/5 rounded-full bg-[#0B1F33]/25" />
                    <div className="mt-2 h-2 w-full rounded-full bg-[#0B1F33]/12" />
                    <div className="mt-2 h-2 w-11/12 rounded-full bg-[#0B1F33]/12" />
                    <div className="mt-4 h-14 rounded-lg" style={{ background: "rgba(25,182,122,0.14)" }} />
                    <div className="mt-3 h-2 w-3/5 rounded-full bg-[#0B1F33]/12" />
                  </div>
                  <p className="mt-4 text-sm font-extrabold">{p.t}</p>
                  <p className="text-xs text-[#42607A]">{p.s}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 text-center">
            <CTA label="Quero os 3 scripts completos" size="lg" />
            <Safety />
          </div>
        </Wrap>
      </section>

      {/* --------------------------------------------------------- scripts -- */}
      <section className="py-16 md:py-24" style={{ background: GREEN_LIGHT }}>
        <Wrap>
          <Reveal className="max-w-3xl">
            <Eyebrow>Os scripts</Eyebrow>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-4xl">
              Você não precisará começar suas conversas do zero
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {SCRIPTS.map((s, i) => (
              <Reveal key={s.n} delay={(i % 3) as 0 | 1 | 2}>
                <div className="h-full rounded-2xl border border-[#0D8F61]/20 bg-white p-6 shadow-[0_18px_44px_-26px_rgba(13,143,97,0.7)]">
                  <span
                    className="inline-flex rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wide text-white"
                    style={{ background: GREEN_DARK }}
                  >
                    {s.n}
                  </span>
                  <h3 className="mt-4 text-lg font-extrabold">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#42607A]">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-center text-sm font-semibold text-[#0D5B41] md:text-base">
            Todos os scripts possuem campos entre colchetes que você adapta à sua oferta,
            ao seu público e ao seu tom de voz.
          </p>
        </Wrap>
      </section>

      {/* ------------------------------------------------------- follow-up -- */}
      <section className="py-16 md:py-24">
        <Wrap className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Eyebrow>Follow-up</Eyebrow>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-4xl">
              Pare de perder vendas só porque o lead parou de responder
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[#42607A] md:text-lg">
              O material ensina a retomar conversas com contexto, utilidade, resposta fácil
              e uma saída respeitosa.
            </p>
            <div className="mt-8">
              <CTA label="Quero vender mais pelo WhatsApp" />
              <Safety />
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div
              className="space-y-3 rounded-3xl border border-[#0B1F33]/10 p-5 shadow-[0_24px_60px_-30px_rgba(11,31,51,0.7)]"
              style={{ background: "#ECE5DD" }}
            >
              {FOLLOWUPS.map((m, i) => (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[88%] rounded-2xl rounded-tr-sm bg-[#D9FDD3] px-4 py-3 shadow-sm">
                    <p className="text-sm leading-relaxed text-[#0B1F33]">{m}</p>
                    <span className="mt-1 block text-right text-[10px] text-[#0B1F33]/50">
                      Exemplo {i + 1} ✓✓
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </Wrap>
      </section>

      {/* ----------------------------------------------------------- prova -- */}
      <section className="bg-[#F7F9FB] py-16 md:py-24">
        <Wrap>
          <Reveal className="max-w-3xl">
            <Eyebrow>Prova e resultados</Eyebrow>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-4xl">
              {CONVERSA.prova.verificada
                ? "Negócios que aplicaram o método registraram aumento médio superior a 200% nas vendas pelo WhatsApp."
                : "Um método criado para aumentar sua conversão, recuperar oportunidades e transformar conversas em um processo comercial."}
            </h2>
            {CONVERSA.prova.verificada && (
              <p className="mt-4 text-sm leading-relaxed text-[#42607A] md:text-base">
                Em uma análise com {CONVERSA.prova.operacoes} operações acompanhadas pela SVG
                Multimídia durante {CONVERSA.prova.periodo}, a aplicação do processo gerou um
                aumento médio de {CONVERSA.prova.resultado} em {CONVERSA.prova.metrica}.
                Contexto: {CONVERSA.prova.contexto}. Resultados individuais podem variar.
              </p>
            )}
          </Reveal>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {CONVERSA.cases.map((c, i) => (
              <Reveal key={i} delay={(i % 3) as 0 | 1 | 2}>
                <article className="flex h-full flex-col rounded-3xl border border-[#0B1F33]/10 bg-white p-6 shadow-[0_20px_50px_-30px_rgba(11,31,51,0.65)]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em]"
                      style={{ background: GREEN_LIGHT, color: GREEN_DARK }}
                    >
                      {c.segmento}
                    </span>
                    <span className="text-xs font-semibold text-[#42607A]">{c.cidade}</span>
                  </div>
                  <h3 className="mt-4 text-xl font-black leading-snug text-[#0B1F33]">
                    {c.resultado}
                  </h3>
                  <dl className="mt-4 space-y-3 border-t border-[#0B1F33]/10 pt-4">
                    <div>
                      <dt className="text-[11px] font-bold uppercase tracking-wide text-[#42607A]">Desafio</dt>
                      <dd className="text-sm leading-relaxed text-[#1D3B55]">{c.desafio}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-bold uppercase tracking-wide text-[#42607A]">O que foi aplicado</dt>
                      <dd className="text-sm leading-relaxed text-[#1D3B55]">{c.aplicacao}</dd>
                    </div>
                  </dl>
                  <div className="mt-5 flex flex-wrap items-center gap-2 pt-1">
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-black text-[#062318]"
                      style={{ background: GREEN }}
                    >
                      {c.metrica}
                    </span>
                    <span className="text-xs font-semibold text-[#42607A]">{c.periodo}</span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-xs text-[#42607A]">
            Cases de operações acompanhadas pela SVG Multimídia. Resultados individuais variam
            conforme oferta, volume de contatos e consistência na aplicação do processo.
          </p>
        </Wrap>
      </section>

      {/* ------------------------------------------------------- para quem -- */}
      <section className="py-16 md:py-24">
        <Wrap>
          <Reveal className="max-w-3xl">
            <Eyebrow>Para quem é</Eyebrow>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-4xl">
              Este método é para você que…
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PARA_QUEM.map((p, i) => (
              <Reveal key={p} delay={(i % 3) as 0 | 1 | 2}>
                <div className="h-full rounded-2xl border border-[#0B1F33]/10 bg-white p-5 shadow-[0_12px_30px_-20px_rgba(11,31,51,0.5)]">
                  <span
                    className="grid h-8 w-8 place-items-center rounded-lg text-sm font-black text-[#062318]"
                    style={{ background: GREEN }}
                  >
                    ✓
                  </span>
                  <p className="mt-3 text-sm font-semibold leading-relaxed text-[#1D3B55]">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p
            className="mt-8 rounded-2xl border-l-4 p-5 text-sm font-semibold md:text-base"
            style={{ background: "#FFF2F0", borderColor: CORAL, color: "#8A2A20" }}
          >
            Este produto não é para quem procura mensagens milagrosas, spam em massa ou
            promessas de venda sem trabalho.
          </p>
        </Wrap>
      </section>

      {/* ----------------------------------------------------------- valor -- */}
      <section className="py-16 md:py-24" style={{ background: NAVY_2 }}>
        <Wrap className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <h2 className="text-3xl font-black tracking-tight text-white md:text-4xl">
              Quanto vale recuperar apenas uma venda que seria perdida?
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[#C6D6E4] md:text-lg">
              Uma única conversa recuperada pode pagar o valor deste material. Além dos scripts,
              você recebe um processo que pode ser utilizado diversas vezes em diferentes ofertas.
            </p>
          </Reveal>
          <Reveal delay={1}>
            <div className="rounded-3xl border border-white/12 bg-white p-6 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.6)]">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#42607A]">
                Valor percebido estimado
              </p>
              <ul className="mt-4 divide-y divide-[#0B1F33]/10">
                {VALOR_STACK.map((v) => (
                  <li key={v.t} className="flex items-center justify-between gap-4 py-3">
                    <span className="text-sm font-semibold text-[#1D3B55]">{v.t}</span>
                    <span className="text-sm font-bold text-[#42607A]">{v.v}</span>
                  </li>
                ))}
                <li className="flex items-center justify-between gap-4 py-3">
                  <span className="text-sm font-black">Valor total percebido</span>
                  <span className="text-sm font-black text-[#42607A] line-through">R$ 300</span>
                </li>
              </ul>
              <p className="mt-2 text-[11px] text-[#42607A]">
                Os itens acima não são vendidos separadamente; os valores representam valor
                percebido estimado.
              </p>
              <div className="mt-5 rounded-2xl p-5 text-center" style={{ background: GREEN_LIGHT }}>
                <p className="text-sm text-[#0D5B41] line-through">De {CONVERSA.precoDe}</p>
                <p className="text-4xl font-black" style={{ color: GREEN_DARK }}>
                  {CONVERSA.precoPor}
                </p>
                {CONVERSA.parcelamento && (
                  <p className="text-xs font-semibold text-[#0D5B41]">{CONVERSA.parcelamento}</p>
                )}
              </div>
              <div className="mt-5">
                <CTA label={`Garantir minha oferta por ${CONVERSA.precoPor}`} full />
              </div>
              <Safety />
            </div>
          </Reveal>
        </Wrap>
      </section>

      {/* -------------------------------------------------------- garantia -- */}
      <section className="py-16 md:py-20">
        <Wrap>
          <Reveal>
            <div className="mx-auto flex max-w-4xl flex-col items-center gap-7 rounded-[28px] border border-[#0B1F33]/10 bg-white p-8 text-center shadow-[0_28px_70px_-40px_rgba(11,31,51,0.75)] md:flex-row md:p-10 md:text-left">
              <GuaranteeSeal />
              <div>
                <h2 className="text-2xl font-black md:text-3xl">
                  Garantia incondicional de {CONVERSA.garantiaDias ?? 7} dias
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#42607A] md:text-base">
                  Compre, leia e aplique o método sem risco. Se em até{" "}
                  {CONVERSA.garantiaDias ?? 7} dias você entender que o material não é para o seu
                  momento, basta solicitar o reembolso — devolvemos 100% do valor pago, sem
                  burocracia e sem perguntas.
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2 md:justify-start">
                  {[
                    "Reembolso integral",
                    "Sem burocracia",
                    "Risco 100% nosso",
                  ].map((t) => (
                    <span
                      key={t}
                      className="rounded-full px-3 py-1.5 text-xs font-bold"
                      style={{ background: GREEN_LIGHT, color: GREEN_DARK }}
                    >
                      ✓ {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={1}>
            <div className="mx-auto mt-6 grid max-w-4xl gap-4 sm:grid-cols-3">
              {[
                { i: <LockIcon />, t: "Compra 100% segura", d: "Ambiente criptografado da Kirvano" },
                { i: <CardIcon />, t: "Pix, cartão ou boleto", d: "Pagamento processado na hora" },
                { i: <BoltIcon />, t: "Acesso imediato", d: "Liberado após a confirmação" },
              ].map((b) => (
                <div
                  key={b.t}
                  className="flex items-center gap-3 rounded-2xl border border-[#0B1F33]/10 bg-white p-4 shadow-[0_14px_36px_-28px_rgba(11,31,51,0.6)]"
                >
                  <span
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-xl"
                    style={{ background: GREEN_LIGHT, color: GREEN_DARK }}
                  >
                    {b.i}
                  </span>
                  <div>
                    <p className="text-sm font-extrabold text-[#0B1F33]">{b.t}</p>
                    <p className="text-xs text-[#42607A]">{b.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </Wrap>
      </section>

      {/* --------------------------------------------------------- urgência -- */}
      <section className="py-14" style={{ background: `linear-gradient(90deg, ${CORAL} 0%, #E0483A 100%)` }}>
        <Wrap className="text-center text-white">
          <h2 className="text-2xl font-black tracking-tight md:text-3xl">
            A condição de lançamento pode terminar em breve
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-white/90 md:text-base">
            O preço promocional de {CONVERSA.precoPor} é válido até{" "}
            {offerDateLabel ?? "[DATA_REAL]"} ou enquanto a condição de lançamento estiver ativa.
          </p>
          <div className="mt-7">
            <a
              href={checkoutHref}
              {...(checkoutExternal ? { target: "_blank", rel: "noopener" } : {})}
              onClick={() => trackCta("urgencia")}
              className="inline-flex items-center justify-center rounded-full bg-white px-8 py-5 text-base font-black uppercase tracking-wide shadow-[0_18px_40px_-16px_rgba(0,0,0,0.5)] transition-transform hover:-translate-y-0.5 md:text-lg"
              style={{ color: NAVY }}
            >
              Garantir meu acesso por {CONVERSA.precoPor}
            </a>
          </div>
        </Wrap>
      </section>

      {/* -------------------------------------------------------------- faq -- */}
      <section className="py-16 md:py-24">
        <Wrap className="max-w-3xl">
          <Eyebrow>Dúvidas frequentes</Eyebrow>
          <h2 className="mt-4 text-3xl font-black tracking-tight md:text-4xl">
            Perguntas frequentes
          </h2>
          <div className="mt-8 divide-y divide-[#0B1F33]/10 overflow-hidden rounded-2xl border border-[#0B1F33]/10 bg-white shadow-[0_16px_40px_-28px_rgba(11,31,51,0.6)]">
            {FAQS.map((f) => (
              <details key={f.q} className="group p-5">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-bold">
                  {f.q}
                  <span
                    aria-hidden
                    className="mt-0.5 text-xl leading-none transition-transform group-open:rotate-45"
                    style={{ color: GREEN_DARK }}
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-[#42607A]">{f.a}</p>
              </details>
            ))}
          </div>
        </Wrap>
      </section>

      {/* --------------------------------------------------------- cta final -- */}
      <section
        className="relative overflow-hidden py-16 md:py-24"
        style={{ background: `radial-gradient(110% 90% at 80% 0%, ${NAVY_2} 0%, ${NAVY} 60%)` }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 rounded-full opacity-25 blur-3xl"
          style={{ background: GREEN }}
        />
        <Wrap className="relative text-center">
          <h2 className="mx-auto max-w-3xl text-3xl font-black leading-tight tracking-tight text-white md:text-5xl">
            Suas próximas vendas podem estar nas conversas que você já tem
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base text-[#C6D6E4] md:text-lg">
            Comece hoje a organizar seu atendimento, apresentar sua oferta com mais clareza e
            acompanhar cada oportunidade com método.
          </p>
          <div className="mt-8 flex flex-wrap items-end justify-center gap-x-4 gap-y-1">
            <span className="text-base text-[#9FB4C7] line-through">De {CONVERSA.precoDe}</span>
            <span className="text-5xl font-black text-white md:text-6xl">{CONVERSA.precoPor}</span>
          </div>
          {CONVERSA.parcelamento && (
            <p className="mt-2 text-sm text-[#C6D6E4]">{CONVERSA.parcelamento}</p>
          )}
          <div className="mt-8">
            <CTA label="Quero acessar o Método CONVERSA" size="lg" />
          </div>
          <p className="mt-3 text-xs font-medium text-[#9FB4C7]">
            Produto digital • Acesso após a confirmação • Compra segura
          </p>
        </Wrap>
      </section>

      {/* ------------------------------------------------------------ rodapé -- */}
      <footer className="pb-28 pt-12 md:pb-12" style={{ background: "#061625" }}>
        <Wrap>
          <p className="text-sm font-bold text-white">
            SVG Multimídia — Agência especialista em conversão por mensagens e estratégias
            comerciais online.
          </p>
          <nav className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#9FB4C7]">
            <Link to={CONVERSA.links.termos} className="hover:text-white">Termos de uso</Link>
            <Link to={CONVERSA.links.privacidade} className="hover:text-white">Política de privacidade</Link>
            <Link to={CONVERSA.links.reembolso} className="hover:text-white">Política de reembolso</Link>
            <Link to={CONVERSA.links.contato} className="hover:text-white">Contato</Link>
            <a href={CONVERSA.links.instagram} target="_blank" rel="noopener" className="hover:text-white">Instagram</a>
            <a href={whatsappUrl(CONVERSA_WHATSAPP_MSG)} target="_blank" rel="noopener" className="hover:text-white">WhatsApp</a>
          </nav>
          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-[#7690A5]">
            Este produto não possui vínculo oficial com WhatsApp ou Meta. WhatsApp é uma marca
            pertencente ao seu respectivo titular. Resultados podem variar conforme oferta,
            mercado, qualidade dos leads e execução.
          </p>
        </Wrap>
      </footer>

      {/* -------------------------------------------------- barra fixa mobile -- */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#0B1F33]/10 bg-white/95 p-3 shadow-[0_-10px_30px_-12px_rgba(11,31,51,0.35)] backdrop-blur md:hidden">
        <div className="flex items-center gap-3">
          <div className="shrink-0 leading-tight">
            <p className="text-[10px] text-[#42607A] line-through">{CONVERSA.precoDe}</p>
            <p className="text-lg font-black" style={{ color: GREEN_DARK }}>{CONVERSA.precoPor}</p>
          </div>
          <a
            href={checkoutHref}
            {...(checkoutExternal ? { target: "_blank", rel: "noopener" } : {})}
            onClick={() => trackCta("barra_mobile")}
            className="flex-1 rounded-full py-3.5 text-center text-xs font-black uppercase tracking-wide text-white"
            style={{ background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)` }}
          >
            Quero vender mais pelo WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}